/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import KeystoneLogo from '../components/KeystoneLogo';
import CashelBlueExportMap from '../components/CashelBlueExportMap';
import { downloadServerPDF } from '../lib/pdf-download';

/**
 * Cashel Blue case study, 4-page A4 deep-dive document.
 *
 * Sent on request when a sceptical buyer asks for "show me one customer in
 * detail". Sister document to the Founding Customer Programme PDF (which is a
 * sales-pitch deck), this one is a customer story.
 *
 * Numbers labelled "before" come from the Excel-and-macros baseline that Cashel
 * Blue's QA & Operations Manager Louis Clifton Brown was building before the
 * Keystone engagement. "After" numbers reflect the live February 2026 deployment.
 */

const COLORS = {
  navy: '#0A2540', ink: '#1F2937', slate: '#475569', muted: '#64748B',
  border: '#E2E8F0', bgSoft: '#F8FAFC', cream: '#FAF8F5', paper: '#FFFFFF',
  magenta: '#E91E63', gold: '#C9A961', green: '#059669',
};

const Page = ({ children, bg = COLORS.paper }) => (
  <div className="brochure-page" style={{
    width: '210mm', height: '297mm',
    background: bg, padding: '12mm 14mm',
    margin: '0 auto 16px',
    pageBreakAfter: 'always', breakAfter: 'page',
    boxSizing: 'border-box', overflow: 'hidden',
    boxShadow: '0 8px 30px rgba(10,37,64,0.08)',
    fontFamily: "'Inter', -apple-system, sans-serif",
    color: COLORS.ink, position: 'relative',
    display: 'flex', flexDirection: 'column',
  }}>{children}</div>
);

const PageHeader = ({ pageNum }) => (
  <div style={{
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    paddingBottom: '3mm', borderBottom: `1px solid ${COLORS.border}`,
    fontSize: '7.5pt', color: COLORS.muted, letterSpacing: '0.18em',
    textTransform: 'uppercase', fontWeight: 700,
  }}>
    <span>Customer story · Cashel Farmhouse Cheesemakers</span>
    <span style={{ color: COLORS.magenta }}>Founding partner</span>
    <span>P. {pageNum} / 05</span>
  </div>
);

const PageFooter = () => (
  <div style={{
    marginTop: 'auto', paddingTop: '3mm',
    borderTop: `1px solid ${COLORS.border}`,
    display: 'flex', justifyContent: 'space-between',
    fontSize: '7pt', color: COLORS.muted,
  }}>
    <span>Keystone by CloudVoro · Cashel Blue Case Study · Feb 2026</span>
    <span>cloudvoro.com · hello@cloudvoro.com</span>
  </div>
);

const SectionLabel = ({ children, color = COLORS.magenta }) => (
  <div style={{
    fontSize: '7.5pt', letterSpacing: '0.22em', textTransform: 'uppercase',
    fontWeight: 700, color, marginBottom: '2mm',
  }}>{children}</div>
);

// =============== Page 1, Cover ===============

const Page1Cover = () => (
  <Page bg={COLORS.cream}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <KeystoneLogo height={32} variant="light" showTagline={false} />
      <img
        src="/customers/cashel-blue-logo.webp"
        alt="Cashel Farmhouse Cheesemakers"
        style={{ height: 56, width: 'auto', objectFit: 'contain' }}
      />
    </div>

    <div style={{ marginTop: '20mm', flex: 1 }}>
      <SectionLabel>Customer story · Founding partner</SectionLabel>
      <h1 style={{
        fontFamily: "'Playfair Display', Georgia, serif",
        fontSize: '34pt', fontWeight: 800, color: COLORS.navy,
        lineHeight: 1.0, letterSpacing: '-0.025em', margin: 0,
      }}>
        How Cashel Blue replaced<br/>
        a complex Excel + macros<br/>
        operation with Keystone,<br/>
        <em style={{ fontStyle: 'italic', color: COLORS.magenta, fontWeight: 700 }}>in thirty days.</em>
      </h1>

      <p style={{ fontSize: '11pt', lineHeight: 1.55, color: COLORS.slate, marginTop: '8mm', maxWidth: '155mm' }}>
        Ireland's most-awarded farmhouse blue cheese had outgrown spreadsheets. Variable-weight wheels, retailer-specific code mapping, batch traceability and split-batch dispatch all lived in increasingly fragile Excel macros. This is the story of how that operation was rebuilt on Keystone, and the numbers behind it.
      </p>

      <div style={{
        marginTop: '12mm', padding: '6mm 8mm',
        background: '#fff', border: `1px solid ${COLORS.border}`,
        borderLeft: `3px solid ${COLORS.magenta}`, borderRadius: 4,
      }}>
        <SectionLabel>The customer at a glance</SectionLabel>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '4mm', marginTop: '3mm',
        }}>
          {[
            ['Established', '1984 · family-run'],
            ['Location', 'Beechmount Farm · Fethard, Co. Tipperary'],
            ['Production', '40+ internal SKUs · variable-weight wheels + cream cheese ranges'],
            ['Distribution', 'International, multi-market export'],
            ['Customer SKU mapping', 'Every export customer has its own private-label SKU, branding & packaging'],
            ['Awards', 'British Cheese Awards · Bord Bia · World Cheese Awards'],
            ['Live on Keystone since', 'February 2026'],
          ].map(([l, v]) => (
            <div key={l}>
              <div style={{ fontSize: '7.5pt', color: COLORS.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.14em' }}>{l}</div>
              <div style={{ fontSize: '10pt', color: COLORS.navy, fontWeight: 600, marginTop: '0.5mm', lineHeight: 1.4 }} dangerouslySetInnerHTML={{ __html: v }} />
            </div>
          ))}
        </div>
      </div>
    </div>

    <PageFooter />
  </Page>
);

// =============== Page 2, International Scale ===============

const Page2International = () => (
  <Page bg={COLORS.cream}>
    <PageHeader pageNum="02" />
    <div style={{ marginTop: '4mm' }}>
      <SectionLabel>International scale · Operational footprint</SectionLabel>
      <h2 style={{
        fontFamily: "'Playfair Display', Georgia, serif",
        fontSize: '20pt', fontWeight: 700, color: COLORS.navy,
        lineHeight: 1.05, letterSpacing: '-0.02em', margin: 0,
      }}>
        From a Tipperary creamery<br/>
        <em style={{ fontStyle: 'italic', color: COLORS.magenta, fontWeight: 600 }}>to international markets.</em>
      </h2>
      <p style={{ fontSize: '9pt', color: COLORS.slate, lineHeight: 1.5, marginTop: '3mm', maxWidth: '170mm' }}>
        Cashel Blue's operational footprint is the reason Keystone is built the way it is. A complex internal product range with every export customer mapped to its own private-label SKU, branding and packaging combination. Dispatched across an international export footprint on three continents. The complexity isn't optional, it's the work. <em style={{ fontSize: '8pt', color: COLORS.muted }}>Cashel Blue<sup>®</sup> and Crozier Blue<sup>®</sup> are registered trademarks of J&L Grubb Ltd; operational footprint cited with written permission.</em>
      </p>
    </div>

    <div style={{ marginTop: '4mm' }}>
      <CashelBlueExportMap variant="light" showTitle={false} showLegend={false} compact />
    </div>

    <div style={{
      marginTop: '4mm',
      padding: '3mm 5mm',
      background: '#fff',
      border: `1px solid ${COLORS.border}`,
      borderLeft: `3px solid ${COLORS.gold}`,
      borderRadius: 4,
    }}>
      <div style={{ fontSize: '7pt', color: COLORS.gold, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: '1.5mm' }}>
        What this complexity means in practice
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '3mm', marginTop: '1mm' }}>
        {[
          { t: '40+ internal SKUs', d: 'Variable-weight wheels and wedges. Every SKU has its own format, weight band, allergen profile and pack size, captured once, reused everywhere.' },
          { t: 'Customer-mapped SKUs', d: 'Every export customer ships under its own private-label SKU code, branding and packaging spec. One batch leaves under multiple customer codes.' },
          { t: 'International distribution', d: 'Multi-market export footprint, each territory with its own retailer rules and traceability evidence.' },
        ].map(c => (
          <div key={c.t}>
            <div style={{ fontSize: '8pt', fontWeight: 800, color: COLORS.navy, marginBottom: '1mm' }}>{c.t}</div>
            <div style={{ fontSize: '7pt', color: COLORS.slate, lineHeight: 1.45 }}>{c.d}</div>
          </div>
        ))}
      </div>
    </div>

    <PageFooter />
  </Page>
);

// =============== Page 3, The Before ===============

const Page2Before = () => (
  <Page>
    <PageHeader pageNum="03" />
    <div style={{ marginTop: '5mm', flex: 1 }}>
      <SectionLabel>The before</SectionLabel>
      <h2 style={{
        fontFamily: "'Playfair Display', Georgia, serif",
        fontSize: '24pt', fontWeight: 700, color: COLORS.navy,
        lineHeight: 1.05, letterSpacing: '-0.02em', margin: 0,
      }}>
        A bespoke Excel-and-macros operation<br/>
        <em style={{ fontStyle: 'italic', color: COLORS.magenta, fontWeight: 600 }}>that nearly worked.</em>
      </h2>

      <p style={{ fontSize: '10pt', lineHeight: 1.55, color: COLORS.slate, marginTop: '5mm' }}>
        Louis Clifton Brown, QA & Operations Manager at Cashel Blue, had built the operational backbone of the dairy in Excel. It worked, but the cracks were visible. Variable-weight orders had to be re-typed twice. Customer-specific product codes, every export market with its own private-label SKU, branding and packaging, lived in a lookup tab that one staff member maintained by hand. Batch traceability ran across three workbooks that had to be reconciled at month-end.
      </p>

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '4mm', marginTop: '6mm', marginBottom: '6mm',
      }}>
        {[
          { metric: '8–12 min', label: 'To produce one dispatch note', sub: 'Manual weight + code lookups' },
          { metric: '3 hrs', label: 'Average batch trace query', sub: 'Across three reconciled workbooks' },
          { metric: '1 person', label: 'Held the operational know-how', sub: 'Key-staff dependency risk' },
          { metric: '0.7%', label: 'Estimated billing leakage', sub: 'Weight + code mismatches' },
          { metric: '~14 days', label: 'Annual audit prep window', sub: 'Compiling BRCGS traceability evidence' },
          { metric: '2 systems', label: 'AccountsIQ + Excel', sub: 'No two-way reconciliation' },
        ].map(m => (
          <div key={m.label} style={{
            padding: '4mm', background: COLORS.bgSoft,
            border: `1px solid ${COLORS.border}`,
            borderTop: `3px solid ${COLORS.muted}`, borderRadius: 4,
          }}>
            <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '20pt', fontWeight: 800, color: COLORS.navy, lineHeight: 1, letterSpacing: '-0.02em' }}>{m.metric}</div>
            <div style={{ fontSize: '8.5pt', fontWeight: 700, color: COLORS.navy, marginTop: '2mm' }}>{m.label}</div>
            <div style={{ fontSize: '7.5pt', color: COLORS.muted, marginTop: '1mm', lineHeight: 1.4 }}>{m.sub}</div>
          </div>
        ))}
      </div>

      <SectionLabel>The triggers that started the conversation</SectionLabel>
      <ul style={{ margin: '2mm 0 0 0', paddingLeft: '5mm', fontSize: '9.5pt', color: COLORS.slate, lineHeight: 1.65 }}>
        <li><strong style={{ color: COLORS.navy }}>Retailer pressure</strong>, increasing demand for faster trace responses to compliance queries and tighter dispatch documentation standards.</li>
        <li><strong style={{ color: COLORS.navy }}>BRCGS traceability requirements</strong> upgrade, supply-chain traceability evidence had to be defensible inside 60 minutes, not 3 hours.</li>
        <li><strong style={{ color: COLORS.navy }}>Operational succession</strong>, Louis was building an even more advanced Excel + macros set-up. The team realised the operational logic shouldn't live in one head and one workbook.</li>
        <li><strong style={{ color: COLORS.navy }}>AccountsIQ limitations</strong>, the team explored extending the accounting package to handle batch traceability. The conclusion: keep operational data separate, integrate the two systems where it adds value.</li>
      </ul>

    </div>
    <PageFooter />
  </Page>
);

// =============== Page 3, The After ===============

const Page3After = () => (
  <Page>
    <PageHeader pageNum="04" />
    <div style={{ marginTop: '5mm', flex: 1 }}>
      <SectionLabel color={COLORS.green}>The after</SectionLabel>
      <h2 style={{
        fontFamily: "'Playfair Display', Georgia, serif",
        fontSize: '24pt', fontWeight: 700, color: COLORS.navy,
        lineHeight: 1.05, letterSpacing: '-0.02em', margin: 0,
      }}>
        Live in 30 days.<br/>
        <em style={{ fontStyle: 'italic', color: COLORS.magenta, fontWeight: 600 }}>The numbers, the workflow, the day-one experience.</em>
      </h2>

      <p style={{ fontSize: '10pt', lineHeight: 1.55, color: COLORS.slate, marginTop: '5mm' }}>
        Implementation started January 2026. The full team, owners Sarah and Sergio Furno, Louis on QA, the dispatch team, gave us 90 minutes a week. We did the data extraction, the customer code mapping, the dispatch templates, the AccountsIQ sync configuration. Cashel Blue went live on Keystone in February 2026 with zero production downtime.
      </p>

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '4mm', marginTop: '5mm', marginBottom: '5mm',
      }}>
        {[
          { metric: '4 sec', label: 'Dispatch note generation', sub: 'Down from 8–12 min, over 99% reduction' },
          { metric: '< 60 sec', label: 'Forward + backward batch trace', sub: 'BRCGS traceability evidence inside 1 minute' },
          { metric: '100%', label: 'Operational logic in system', sub: 'No more single-point-of-failure on staff' },
          { metric: '0', label: 'Spreadsheets in dispatch', sub: 'Replaced from go-live day one' },
          { metric: 'Live', label: 'AccountsIQ two-way sync', sub: 'Customers in · invoices out' },
          { metric: '~2 hrs', label: 'Audit-pack export window', sub: 'Down from ~14 days · ~95% reduction' },
        ].map(m => (
          <div key={m.label} style={{
            padding: '4mm', background: '#fff',
            border: `1px solid ${COLORS.border}`,
            borderTop: `3px solid ${COLORS.green}`, borderRadius: 4,
          }}>
            <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '20pt', fontWeight: 800, color: COLORS.green, lineHeight: 1, letterSpacing: '-0.02em' }}>{m.metric}</div>
            <div style={{ fontSize: '8.5pt', fontWeight: 700, color: COLORS.navy, marginTop: '2mm' }}>{m.label}</div>
            <div style={{ fontSize: '7.5pt', color: COLORS.muted, marginTop: '1mm', lineHeight: 1.4 }}>{m.sub}</div>
          </div>
        ))}
      </div>

      <SectionLabel>What changed on the floor</SectionLabel>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6mm', marginTop: '2mm' }}>
        <div>
          <div style={{ fontSize: '9pt', fontWeight: 700, color: COLORS.navy, marginBottom: '2mm' }}>Order entry & dispatch</div>
          <ul style={{ margin: 0, paddingLeft: '4mm', fontSize: '9pt', color: COLORS.slate, lineHeight: 1.6 }}>
            <li>Customer-specific code mapping with reverse lookup</li>
            <li>Variable-weight batch capture at scan-in</li>
            <li>Split-batch dispatch with auto-reconciliation</li>
            <li>Global dispatch search by document number</li>
            <li>Use-by date tracking by batch</li>
          </ul>
        </div>
        <div>
          <div style={{ fontSize: '9pt', fontWeight: 700, color: COLORS.navy, marginBottom: '2mm' }}>Compliance & reporting</div>
          <ul style={{ margin: 0, paddingLeft: '4mm', fontSize: '9pt', color: COLORS.slate, lineHeight: 1.6 }}>
            <li>BRCGS traceability traceability evidence on demand</li>
            <li>FSAI / SFPA audit-pack export</li>
            <li>Forward + backward batch trace in one query</li>
            <li>Append-only audit log on all operational changes</li>
            <li>Automated month-end reconciliation to AccountsIQ</li>
          </ul>
        </div>
      </div>
    </div>
    <PageFooter />
  </Page>
);

// =============== Page 4, Lessons + CTA ===============

const Page4Lessons = () => (
  <Page bg={COLORS.navy}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <KeystoneLogo height={32} variant="dark" showTagline={false} />
      <div style={{
        fontSize: '7.5pt', letterSpacing: '0.18em', textTransform: 'uppercase',
        fontWeight: 700, color: COLORS.gold,
      }}>P. 05 / 05, Conclusion & next steps</div>
    </div>

    <div style={{ marginTop: '14mm', flex: 1, color: '#fff' }}>
      <SectionLabel color={COLORS.gold}>What this teaches us</SectionLabel>
      <h2 style={{
        fontFamily: "'Playfair Display', Georgia, serif",
        fontSize: '24pt', fontWeight: 700, color: '#fff',
        lineHeight: 1.05, letterSpacing: '-0.02em', margin: 0,
      }}>
        Six product features<br/>
        <em style={{ fontStyle: 'italic', color: COLORS.gold, fontWeight: 600 }}>that exist because of this customer.</em>
      </h2>

      <p style={{ fontSize: '10.5pt', lineHeight: 1.6, color: '#CBD5E0', marginTop: '5mm' }}>
        Every artisan dairy is operationally specific. The features below were built by us in direct response to floor-team feedback at Cashel Blue, and now ship as standard for every Keystone customer who follows them.
      </p>

      <ol style={{ margin: '6mm 0 0 0', paddingLeft: '6mm', fontSize: '10.5pt', color: '#fff', lineHeight: 1.7 }}>
        <li style={{ marginBottom: '2.5mm' }}><strong style={{ color: COLORS.gold }}>Variable-weight batch capture.</strong> Wheels are weighed individually; the system reconciles to the dispatched line automatically.</li>
        <li style={{ marginBottom: '2.5mm' }}><strong style={{ color: COLORS.gold }}>Retailer code mapping with reverse lookup.</strong> One internal SKU → many retailer-specific codes, searchable in either direction.</li>
        <li style={{ marginBottom: '2.5mm' }}><strong style={{ color: COLORS.gold }}>Split-batch dispatch.</strong> One order can pull from multiple batches; trace records on both sides match automatically.</li>
        <li style={{ marginBottom: '2.5mm' }}><strong style={{ color: COLORS.gold }}>Use-by tracking by batch.</strong> Production date in, use-by out, no manual recalculation, no hand-typed labels.</li>
        <li style={{ marginBottom: '2.5mm' }}><strong style={{ color: COLORS.gold }}>AccountsIQ two-way sync.</strong> Customers and credits flow in; dispatch-completed invoices flow out. Operations and accounts stay separate but reconciled.</li>
        <li style={{ marginBottom: '2.5mm' }}><strong style={{ color: COLORS.gold }}>Global dispatch search.</strong> Document number search across the whole pipeline (the actual feature request that started it: Louis emailing about needing to find a dispatch note in a busy week).</li>
      </ol>

      <div style={{
        marginTop: '10mm', padding: '6mm 8mm',
        background: 'rgba(201, 169, 97, 0.08)',
        border: `1px solid ${COLORS.gold}40`,
        borderLeft: `3px solid ${COLORS.gold}`,
        borderRadius: 4,
      }}>
        <SectionLabel color={COLORS.gold}>Want to talk to Cashel Blue directly?</SectionLabel>
        <p style={{ fontSize: '11pt', lineHeight: 1.55, color: '#fff', margin: 0 }}>
          We can broker an introductory call with the QA & Operations Manager once we've had a brief discovery conversation. No pitch, no script, they tell you what works and what doesn't.
        </p>
        <div style={{ marginTop: '5mm', display: 'flex', gap: '6mm', flexWrap: 'wrap', alignItems: 'center' }}>
          <a href="https://calendly.com/asif-cloudvoro/30min" style={{ fontSize: '11pt', color: COLORS.gold, fontWeight: 700, textDecoration: 'none' }}>
            calendly.com/asif-cloudvoro/30min →
          </a>
          <span style={{ color: '#475569' }}>·</span>
          <a href="mailto:hello@cloudvoro.com" style={{ fontSize: '11pt', color: COLORS.gold, fontWeight: 700, textDecoration: 'none' }}>
            hello@cloudvoro.com
          </a>
          <span style={{ color: '#475569' }}>·</span>
          <span style={{ fontSize: '11pt', color: '#fff', fontWeight: 700 }}>+353 85 181 0310</span>
        </div>
      </div>
    </div>

    <div style={{
      borderTop: `1px solid rgba(255,255,255,0.15)`, paddingTop: '3mm', marginTop: '5mm',
      display: 'flex', justifyContent: 'space-between', fontSize: '7.5pt', color: '#94A3B8',
    }}>
      <span>Keystone by CloudVoro · Cashel Blue Case Study · Feb 2026</span>
      <span>Customer name & logo used with permission. Numbers verified at time of writing.</span>
    </div>
  </Page>
);

// =============== Toolbar + entry ===============

const Toolbar = () => {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      await downloadServerPDF('/case-study/cashel-blue', 'Keystone-Cashel-Blue-Case-Study.pdf');
    } catch (err) {
      console.error('PDF export failed:', err);
      alert('PDF export failed. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="no-print" style={{
      position: 'sticky', top: 0, zIndex: 100,
      background: '#fff', borderBottom: `1px solid ${COLORS.border}`,
      padding: '12px 24px', display: 'flex',
      justifyContent: 'space-between', alignItems: 'center',
      boxShadow: '0 2px 8px rgba(10,37,64,0.06)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <a href="/" style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '8px 14px', background: COLORS.bgSoft,
          color: COLORS.navy, border: `1px solid ${COLORS.border}`,
          borderRadius: 6, fontSize: 12.5, fontWeight: 600, textDecoration: 'none',
        }} data-testid="case-study-back-to-site">
          ← Back to site
        </a>
        <div style={{ borderLeft: `1px solid ${COLORS.border}`, paddingLeft: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
          <KeystoneLogo height={36} variant="light" showTagline={false} />
          <div style={{ borderLeft: `1px solid ${COLORS.border}`, paddingLeft: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.navy }}>Cashel Blue Case Study</div>
            <div style={{ fontSize: 11, color: COLORS.muted }}>4 pages · A4 · Customer story</div>
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <button onClick={() => window.print()} data-testid="case-study-print-btn" style={{
          background: '#fff', color: COLORS.navy,
          border: `1px solid ${COLORS.border}`,
          padding: '9px 16px', borderRadius: 6,
          fontSize: 13, fontWeight: 600, cursor: 'pointer',
        }}>🖨  Print</button>
        <button
          onClick={handleDownload}
          disabled={downloading}
          data-testid="case-study-download-pdf-btn"
          style={{
            background: downloading ? COLORS.muted : COLORS.magenta,
            color: '#fff', border: 'none',
            padding: '10px 22px', borderRadius: 6,
            fontSize: 13, fontWeight: 700,
            cursor: downloading ? 'wait' : 'pointer',
            letterSpacing: '0.02em',
            boxShadow: '0 4px 12px rgba(233,30,99,0.3)',
          }}
        >
          {downloading ? '⏳  Generating PDF…' : '⬇  Download PDF'}
        </button>
      </div>
    </div>
  );
};

export default function CashelBlueCaseStudyPDF() {
  return (
    <div style={{ minHeight: '100vh', background: '#E8EAEC', padding: '0 0 60px' }}>
      <style>{`
        @media print {
          @page { size: A4; margin: 0; }
          body { background: white !important; margin: 0 !important; padding: 0 !important; }
          .no-print { display: none !important; }
          .brochure-stack { padding: 0 !important; }
          .brochure-page {
            box-shadow: none !important;
            margin: 0 !important;
            box-sizing: border-box !important;
            page-break-after: always;
            break-after: page;
            border: none !important;
          }
          .brochure-page:last-of-type {
            page-break-after: auto !important;
            break-after: auto !important;
          }
        }
      `}</style>
      <Toolbar />
      <div className="brochure-stack" style={{ padding: '24px 0' }}>
        <Page1Cover />
        <Page2International />
        <Page2Before />
        <Page3After />
        <Page4Lessons />
      </div>
    </div>
  );
}
